import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, XAxis } from "recharts"

import { ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart"
import { formatMoney } from "@/lib/formatters"
import { ChartFrame, type ChartFrameRow } from "@/features/staff/reports/charts/chart-frame"
import type { ForecastChartPoint } from "@/features/staff/reports/charts/forecast-series"
import { tickInterval } from "@/features/staff/reports/charts/series"
import { tSharedReports } from "@/features/staff/reports/format"

/**
 * Real sales, then the model's own estimate of what comes next, on one axis.
 *
 * **No colour tells the two halves apart.** Both lines are `--foreground`; what
 * distinguishes "this happened" from "this is a guess" is solid vs
 * `strokeDasharray`, a filled dot vs a hollow one, and a labelled `ReferenceLine`
 * marking the seam — every one of them a shape, so the distinction survives
 * grayscale and every kind of colour blindness the way a red/green legend would
 * not. The interval band is the one thing that IS shaded, in `--muted`, and it
 * only ever appears under the dashed half: a band under real data would draw
 * uncertainty around a number that has none.
 *
 * `connectNulls={false}` on both `<Line>`s is load-bearing. `buildForecastChartSeries`
 * gives the actual series `expected: null` everywhere except its very last point
 * (the seam, where it is deliberately set equal to `actual`) and the forecast
 * series `actual: null` everywhere — so with `connectNulls` at its recharts
 * default of `true`, the solid line would bridge straight through every future
 * bucket and the dashed line would bridge straight back through every historical
 * one, and the whole point of drawing two lines instead of one would be undone
 * by the prop that is supposed to be an optimisation.
 */

const FORECAST_CONFIG = {
  actual: { color: "var(--foreground)" },
  expected: { color: "var(--foreground)" },
  band: { color: "var(--muted)" },
} satisfies ChartConfig

type TooltipPayload = { payload?: ForecastChartPoint }

function forecastTooltipFormatter(_value: unknown, _name: unknown, item: TooltipPayload) {
  const point = item?.payload
  if (!point) return null

  // A hovered bucket is either real or estimated, never both — the seam point
  // carries both values internally so the lines meet, but the tooltip still
  // has to say which single fact it is reporting.
  if (point.actualAmount !== null) {
    return (
      <span className="flex flex-1 items-center justify-between gap-4">
        <span className="text-muted-foreground">{tSharedReports("forecast.chart_actual_label")}</span>
        <span className="font-semibold tabular-nums text-foreground">
          {formatMoney(point.actualAmount)}
        </span>
      </span>
    )
  }

  if (point.expectedAmount === null) return null

  return (
    <div className="flex flex-1 flex-col gap-0.5">
      <span className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">{tSharedReports("forecast.chart_expected_label")}</span>
        <span className="font-semibold tabular-nums text-foreground">
          {formatMoney(point.expectedAmount)}
        </span>
      </span>
      {point.lowerAmount !== null && point.upperAmount !== null ? (
        <span className="text-muted-foreground">
          {tSharedReports("forecast.chart_band", {
            lower: formatMoney(point.lowerAmount),
            upper: formatMoney(point.upperAmount),
          })}
        </span>
      ) : null}
    </div>
  )
}

export function ForecastChart({
  points,
  seamKey,
  note,
  testId,
}: {
  points: ForecastChartPoint[]
  /**
   * The last real bucket's `tick` — where the `ReferenceLine` and its label
   * land. It has to be the tick and not the key: `x` on a `ReferenceLine` is
   * matched against the value of the axis' own `dataKey`, which is `tick` here,
   * and recharts drops a reference line that matches no category without
   * complaining. `null` when the chart draws no history at all.
   */
  seamKey: string | null
  note?: string
  testId: string
}) {
  const rows: ChartFrameRow[] = points.map((point) => ({
    key: point.key,
    cells: [
      point.label,
      point.actualAmount !== null ? formatMoney(point.actualAmount) : "—",
      point.expectedAmount !== null && point.actualAmount === null
        ? formatMoney(point.expectedAmount)
        : "—",
      point.lowerAmount !== null && point.actualAmount === null
        ? formatMoney(point.lowerAmount)
        : "—",
      point.upperAmount !== null && point.actualAmount === null
        ? formatMoney(point.upperAmount)
        : "—",
    ],
  }))

  return (
    <ChartFrame
      label={tSharedReports("forecast.chart_label")}
      note={note}
      config={FORECAST_CONFIG}
      testId={testId}
      columns={[
        { key: "bucket", label: tSharedReports("forecast.chart_date_column") },
        { key: "actual", label: tSharedReports("forecast.chart_actual_label") },
        { key: "expected", label: tSharedReports("forecast.chart_expected_label") },
        { key: "lower", label: tSharedReports("forecast.chart_lower_column") },
        { key: "upper", label: tSharedReports("forecast.chart_upper_column") },
      ]}
      rows={rows}
    >
      <ComposedChart
        accessibilityLayer
        data={points}
        margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="tick"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={tickInterval(points.length)}
        />

        <ChartTooltip
          content={
            <ChartTooltipContent
              hideIndicator
              labelFormatter={(_value, payload) =>
                (payload?.[0]?.payload as ForecastChartPoint | undefined)?.label ?? ""
              }
              formatter={forecastTooltipFormatter}
            />
          }
        />

        {seamKey ? (
          <ReferenceLine
            x={seamKey}
            stroke="var(--muted-foreground)"
            strokeDasharray="2 2"
            label={{
              value: tSharedReports("forecast.chart_seam_label"),
              position: "insideTopRight",
              fill: "var(--muted-foreground)",
              fontSize: 11,
            }}
          />
        ) : null}

        {/* The band: an invisible base up to `lower`, then a visible fill from
            `lower` to `upper`, stacked so the second area starts exactly where
            the first one stops. `null` on every real bucket, so the fill never
            reaches back over data that carries no interval. */}
        <Area
          dataKey="lowerBase"
          stackId="band"
          stroke="none"
          fill="transparent"
          isAnimationActive={false}
          connectNulls={false}
        />
        <Area
          dataKey="bandHeight"
          stackId="band"
          stroke="none"
          fill="var(--muted)"
          isAnimationActive={false}
          connectNulls={false}
        />

        {/* Solid, filled dots: what happened. */}
        <Line
          dataKey="actual"
          stroke="var(--color-actual)"
          strokeWidth={2}
          dot={{ r: 2.5, fill: "var(--color-actual)", strokeWidth: 0 }}
          activeDot={{ r: 4 }}
          connectNulls={false}
          isAnimationActive={false}
        />
        {/* Dashed, hollow dots: the model's guess. Same ink as `actual` —
            geometry carries the distinction, not colour. */}
        <Line
          dataKey="expected"
          stroke="var(--color-expected)"
          strokeWidth={2}
          strokeDasharray="4 4"
          dot={{ r: 2.5, fill: "var(--background)", stroke: "var(--color-expected)", strokeWidth: 1.5 }}
          activeDot={{ r: 4 }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartFrame>
  )
}
